import { useQuery } from '@tanstack/react-query'
import { CalendarPlus, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toApiError } from '@/api/errors'
import { Alert } from '@/components/ui/Alert'
import { Button, IconButton } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Dialog } from '@/components/ui/Dialog'
import { FormField } from '@/components/forms/FormField'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { classesQuery } from '@/features/academic/api'
import { staffListQuery } from '@/features/staff/api'
import { coursesQuery, useSaveRun } from '../api'
import { weekdayName } from '../format'
import type { CourseRun, Slot } from '../types'

interface RunDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Preselected course (from its page). */
  courseId?: number
  /** Editing an existing run. */
  run?: CourseRun
}

/** Schedules a course for a group: dates, teacher and weekly sessions. */
export function RunDialog({ open, onOpenChange, courseId, run }: RunDialogProps) {
  const { t } = useTranslation('courses')
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<CalendarPlus />}
      title={run ? t('Edit the schedule') : t('Schedule a course')}
      description={t(
        'The same course can run for several groups, each with its own dates and weekly sessions.',
      )}
      size="lg"
    >
      {open ? <RunForm courseId={courseId} run={run} onDone={() => onOpenChange(false)} /> : null}
    </Dialog>
  )
}

function RunForm({ courseId, run, onDone }: { courseId?: number; run?: CourseRun; onDone: () => void }) {
  const { t } = useTranslation('courses')
  const navigate = useNavigate()
  const courses = useQuery(coursesQuery())
  const classes = useQuery(classesQuery())
  const staff = useQuery(staffListQuery({ page: 1, perPage: 200, status: 'active' }))
  const save = useSaveRun()
  const [course, setCourse] = useState(String(run?.course.id ?? courseId ?? ''))
  const [classId, setClassId] = useState(String(run?.section.classId ?? ''))
  const [sectionId, setSectionId] = useState(String(run?.section.id ?? ''))
  const [teacherId, setTeacherId] = useState(String(run?.teacher?.id ?? ''))
  const [startsOn, setStartsOn] = useState(run?.startsOn ?? '')
  const [endsOn, setEndsOn] = useState(run?.endsOn ?? '')
  const [notes, setNotes] = useState(run?.notes ?? '')
  const [copy, setCopy] = useState(true)
  const [slots, setSlots] = useState<Slot[]>(
    run?.slots ?? [{ dayOfWeek: 1, startsAt: '18:00', endsAt: '20:00', room: '' }],
  )
  const [error, setError] = useState<string | null>(null)
  const sections = classes.data?.find((c) => String(c.id) === classId)?.sections ?? []
  const patch = (i: number, changes: Partial<Slot>) =>
    setSlots((all) => all.map((s, k) => (k === i ? { ...s, ...changes } : s)))

  const submit = async () => {
    setError(null)
    try {
      const saved = await save.mutateAsync({
        id: run?.id,
        courseId: Number(course),
        sectionId: Number(sectionId),
        teacherId: teacherId ? Number(teacherId) : null,
        startsOn,
        endsOn,
        notes: notes || null,
        slots: slots.map((s) => ({ ...s, room: s.room || null })),
        copyLessons: run ? undefined : copy,
      })
      onDone()
      if (!run) void navigate(`/admin/courses/runs/${saved.id}`)
    } catch (e) {
      setError(toApiError(e).message)
    }
  }

  return (
    <div className="space-y-4">
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label={t('Course')} required className="sm:col-span-2">
          <Select
            placeholder={t('Choose a course')}
            value={course}
            disabled={run !== undefined}
            onChange={(e) => setCourse(e.target.value)}
            options={(courses.data ?? []).map((c) => ({
              value: c.id,
              label: c.code ? `${c.name} (${c.code})` : c.name,
            }))}
          />
        </FormField>
        <FormField label={t('Class')} required>
          <Select
            placeholder={t('Choose')}
            value={classId}
            onChange={(e) => {
              setClassId(e.target.value)
              setSectionId('')
            }}
            options={(classes.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
          />
        </FormField>
        <FormField label={t('Section')} required>
          <Select
            placeholder={t('Choose')}
            value={sectionId}
            disabled={!classId}
            onChange={(e) => setSectionId(e.target.value)}
            options={sections.map((s) => ({ value: s.id, label: s.name || t('Whole class') }))}
          />
        </FormField>
        <FormField label={t('Teacher')} className="sm:col-span-2">
          <Select
            placeholder={t('No teacher yet')}
            value={teacherId}
            onChange={(e) => setTeacherId(e.target.value)}
            options={(staff.data?.data ?? []).map((s) => ({ value: s.id, label: s.fullName }))}
          />
        </FormField>
        <FormField label={t('Starts on')} required>
          <Input type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
        </FormField>
        <FormField label={t('Ends on')} required>
          <Input
            type="date"
            value={endsOn}
            min={startsOn || undefined}
            onChange={(e) => setEndsOn(e.target.value)}
          />
        </FormField>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-sm font-medium text-ink">{t('Weekly sessions')}</p>
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Plus />}
            onClick={() =>
              setSlots((all) => [...all, { dayOfWeek: 3, startsAt: '18:00', endsAt: '20:00', room: '' }])
            }
          >
            {t('Add a session')}
          </Button>
        </div>
        <ul className="space-y-2">
          {slots.map((s, i) => (
            <li
              key={i}
              className="grid grid-cols-2 items-end gap-2 rounded-control border border-line p-2 sm:grid-cols-[1fr_7rem_7rem_1fr_auto]"
            >
              <FormField label={t('Day')}>
                <Select
                  value={String(s.dayOfWeek)}
                  onChange={(e) => patch(i, { dayOfWeek: Number(e.target.value) })}
                  options={[1, 2, 3, 4, 5, 6, 7].map((d) => ({ value: d, label: weekdayName(d) }))}
                />
              </FormField>
              <FormField label={t('From')}>
                <Input
                  type="time"
                  value={s.startsAt}
                  onChange={(e) => patch(i, { startsAt: e.target.value })}
                />
              </FormField>
              <FormField label={t('To')}>
                <Input type="time" value={s.endsAt} onChange={(e) => patch(i, { endsAt: e.target.value })} />
              </FormField>
              <FormField label={t('Room')}>
                <Input
                  value={s.room ?? ''}
                  onChange={(e) => patch(i, { room: e.target.value })}
                  maxLength={80}
                />
              </FormField>
              <IconButton
                label={t('Remove')}
                icon={<Trash2 />}
                variant="ghost"
                disabled={slots.length === 1}
                onClick={() => setSlots((all) => all.filter((_, k) => k !== i))}
              />
            </li>
          ))}
        </ul>
      </div>

      <FormField label={t('Notes')}>
        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} />
      </FormField>
      {!run ? (
        <label className="flex items-start gap-2 text-sm text-ink-soft">
          <Checkbox checked={copy} onCheckedChange={(v) => setCopy(v === true)} className="mt-0.5" />
          {t("Copy the course content into the group's online learning")}
        </label>
      ) : null}
      <div className="flex justify-end gap-2 border-t border-line pt-4">
        <Button variant="secondary" onClick={onDone}>
          {t('Cancel')}
        </Button>
        <Button
          onClick={() => void submit()}
          loading={save.isPending}
          disabled={!course || !sectionId || !startsOn || !endsOn}
        >
          {run ? t('Save') : t('Schedule')}
        </Button>
      </div>
    </div>
  )
}
